import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { ConfigProvider, App } from 'antd';
import { getTheme, type ThemeMode } from '@/config/theme';

interface ThemeContextType {
  themeMode: ThemeMode;
  toggleTheme: () => void;
  isDark: boolean;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
};

interface ThemeProviderProps {
  children: ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [themeMode, setThemeMode] = useState<ThemeMode>(() => {
    // Initialize theme from localStorage or default to light
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem('admin-theme-mode');
      if (stored && (stored === 'light' || stored === 'dark')) {
        return stored as ThemeMode;
      }
      // Check system preference
      if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
        return 'dark';
      }
    }
    return 'light';
  });

  const toggleTheme = () => {
    const newMode = themeMode === 'light' ? 'dark' : 'light';
    setThemeMode(newMode);
  };

  const isDark = themeMode === 'dark';

  // Persist theme preference and apply to document
  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('admin-theme-mode', themeMode);
      
      // Apply theme class to document for CSS variables
      document.documentElement.classList.remove('light', 'dark');
      document.documentElement.classList.add(themeMode);
      
      // Update meta theme-color for mobile browsers
      const metaThemeColor = document.querySelector('meta[name="theme-color"]');
      if (metaThemeColor) {
        metaThemeColor.setAttribute('content', isDark ? '#1f2937' : '#ffffff');
      }
    }
  }, [themeMode, isDark]);

  // Listen to system theme changes
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      
      const handleChange = (e: MediaQueryListEvent) => {
        // Only update if user hasn't manually set a preference
        const stored = localStorage.getItem('admin-theme-mode');
        if (!stored) {
          setThemeMode(e.matches ? 'dark' : 'light');
        }
      };

      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }
  }, []);

  const contextValue: ThemeContextType = {
    themeMode,
    toggleTheme,
    isDark,
  };

  return (
    <ThemeContext.Provider value={contextValue}>
      <ConfigProvider
        theme={getTheme(themeMode)}
        prefixCls="ant"
        iconPrefixCls="anticon"
        getPrefixCls={(suffix, customPrefix) => customPrefix || `ant-${suffix}`}
        componentSize="middle"
        wave={{ disabled: false }}
      >
        <App
          message={{ maxCount: 3 }}
          notification={{ 
            maxCount: 5, 
            placement: 'topRight',
            showProgress: true,
          }}
        >
          <div className={`min-h-screen transition-colors duration-200 ${isDark ? 'dark' : ''}`}>
            {children}
          </div>
        </App>
      </ConfigProvider>
    </ThemeContext.Provider>
  );
};